import GameObjectType from '../../GameObject/GameObject';

declare global {
    type GameObject<T extends PIXI.Container = PIXI.Container> = GameObjectType<T>;
}

declare module 'pixi.js' {
    export interface NineSlicePlane extends PIXI.mesh.NineSlicePlane {
    }
}

type IPointLike = { x: number; y: number };

export enum ModelType {
    Container = 'Container',
    Sprite = 'Sprite',
    Text = 'Text',
    Graphics = 'Graphics',
    NineSlicePlane = 'NineSlicePlane',
    Layout = 'Layout',
    Armature = 'Armature'
}

export type CommonModel = {
    type: ModelType;
    id: string;
    name: string;
    nameSpace: string;
    className: string;
    x: number;
    y: number;
    scale: IPointLike;
    angle: number;
    alpha: number;
    interactive: boolean;
    buttonMode: boolean;
    hitArea?: HitArea;
    visible: boolean;
    parent: string;
    data: string;
};

export type ContainerModel = CommonModel & {
    type: ModelType.Container;
    pivot: IPointLike;
};

export type SpriteModel = CommonModel & {
    type: ModelType.Sprite;
    anchor: IPointLike;
    tint: number;
    blendMode: number;
    texture: string;
};

export type TextIconModel = {
    key: string;
    x: number;
    y: number;
    scale: IPointLike;
    texture: string;
};

export type TextModel = CommonModel & {
    type: ModelType.Text;
    anchor: IPointLike;
    blendMode: number;
    text: string;
    localize: boolean;
    maxWidth: number;
    maxHeight: number;
    maxFontSize: number;
    icons: TextIconModel[];
    style: PIXI.TextStyle;
};

export type GraphicsModel = CommonModel & {
    type: ModelType.Graphics;
    pivot: IPointLike;
    tint: number;
    blendMode: number;
    bake: boolean;
    commands: GraphicsCommand[];
};

export type NineSlicePlaneModel = CommonModel & {
    type: ModelType.NineSlicePlane;
    scale: IPointLike;
    pivot: IPointLike;
    width: number;
    height: number;
    leftWidth: number;
    topHeight: number;
    rightWidth: number;
    bottomHeight: number;
    tint: number;
    blendMode: number;
    texture: string;
};

export type ArmatureModel = CommonModel & {
    type: ModelType.Armature;
    scale: IPointLike;
    pivot: IPointLike;
    width: number;
    height: number;
    tint: number;
    blendMode: number;
    animationName: string;
    armatureName: string;
    dragonbonesName: string;
    playOnStart: boolean;
    progress: number;
    playTimes: number;
}

export type LayoutModel = CommonModel & {
    type: ModelType.Layout;
    path: string;
    percentX: number;
    percentY: number;
    handleResize: boolean;
    pivot: IPointLike;
};

export type Model =
    | ContainerModel
    | SpriteModel
    | TextModel
    | GraphicsModel
    | NineSlicePlaneModel
    | LayoutModel;

export type Raw = {
    x: number;
    y: number;
    list: Model[];
    width: number;
    height: number;
    [key:string]:any;
};

export enum GraphicsCommandType {
    BeginFill = 'beginFill',
    LineStyle = 'lineStyle',
    DrawRect = 'drawRect',
    DrawRoundedRect = 'drawRoundedRect',
    DrawCircle = 'drawCircle',
    DrawEllipse = 'drawEllipse',
    DrawStar = 'drawStar',
    DrawPolygon = 'drawPolygon',
    MoveTo = 'moveTo',
    LineTo = 'lineTo',
    Arc = 'arc',
    ArcTo = 'arcTo',
    QuadraticCurveTo = 'quadraticCurveTo',
    BezierCurveTo = 'bezierCurveTo',
    BeginHole = 'beginHole',
    EndHole = 'endHole',
    ClosePath = 'closePath',
    EndFill = 'endFill',
}

export type GraphicsBeginFillCommand = {
    type: GraphicsCommandType.BeginFill;
    color: number;
    alpha: number;
};

export type GraphicsLineStyleCommand = {
    type: GraphicsCommandType.LineStyle;
    width: number;
    color: number;
    alpha: number;
    alignment: number;
    native: boolean;
};

export type GraphicsDrawRectCommand = {
    type: GraphicsCommandType.DrawRect;
    x: number;
    y: number;
    width: number;
    height: number;
};

export type GraphicsDrawRoundedRectCommand = {
    type: GraphicsCommandType.DrawRoundedRect;
    x: number;
    y: number;
    width: number;
    height: number;
    radius: number;
};

export type GraphicsDrawCircleCommand = {
    type: GraphicsCommandType.DrawCircle;
    x: number;
    y: number;
    radius: number;
};

export type GraphicsDrawEllipseCommand = {
    type: GraphicsCommandType.DrawEllipse;
    x: number;
    y: number;
    width: number;
    height: number;
};

export type GraphicsDrawStarCommand = {
    type: GraphicsCommandType.DrawStar;
    x: number;
    y: number;
    points: number;
    radius: number;
    innerRadius: number;
    rotation: number;
};

export type GraphicsDrawPolygonCommand = {
    type: GraphicsCommandType.DrawPolygon;
    path: string;
};

export type GraphicsMoveToCommand = {
    type: GraphicsCommandType.MoveTo;
    x: number;
    y: number;
};

export type GraphicsLineToCommand = {
    type: GraphicsCommandType.LineTo;
    x: number;
    y: number;
};

export type GraphicsArcCommand = {
    type: GraphicsCommandType.Arc;
    cx: number;
    cy: number;
    radius: number;
    startAngle: number;
    endAngle: number;
    anticlockwise: boolean;
};

export type GraphicsArcToCommand = {
    type: GraphicsCommandType.ArcTo;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    radius: number;
};

export type GraphicsQuadraticCurveToCommand = {
    type: GraphicsCommandType.QuadraticCurveTo;
    cpX: number;
    cpY: number;
    toX: number;
    toY: number;
};

export type GraphicsBezierCurveToCommand = {
    type: GraphicsCommandType.BezierCurveTo;
    cpX: number;
    cpY: number;
    cpX2: number;
    cpY2: number;
    toX: number;
    toY: number;
};

export type GraphicsBeginHoleCommand = {
    type: GraphicsCommandType.BeginHole;
};

export type GraphicsEndHoleCommand = {
    type: GraphicsCommandType.EndHole;
};

export type GraphicsClosePathCommand = {
    type: GraphicsCommandType.ClosePath;
};

export type GraphicsEndFillCommand = {
    type: GraphicsCommandType.EndFill;
};

export type GraphicsCommand =
    | GraphicsBeginFillCommand
    | GraphicsLineStyleCommand
    | GraphicsDrawRectCommand
    | GraphicsDrawRoundedRectCommand
    | GraphicsDrawCircleCommand
    | GraphicsDrawEllipseCommand
    | GraphicsDrawStarCommand
    | GraphicsDrawPolygonCommand
    | GraphicsMoveToCommand
    | GraphicsLineToCommand
    | GraphicsArcCommand
    | GraphicsArcToCommand
    | GraphicsQuadraticCurveToCommand
    | GraphicsBezierCurveToCommand
    | GraphicsBeginHoleCommand
    | GraphicsEndHoleCommand
    | GraphicsClosePathCommand
    | GraphicsEndFillCommand;

export enum HitAreaType {
    None = 'None',
    Rectangle = 'Rectangle',
    Circle = 'Circle',
    Ellipse = 'Ellipse',
    Polygon = 'Polygon',
    RoundedRectangle = 'RoundedRectangle',
}

export type NoneHitArea = {
    type: HitAreaType.None;
};

export type RectangleHitArea = {
    type: HitAreaType.Rectangle;
    x: number;
    y: number;
    width: number;
    height: number;
};

export type CircleHitArea = {
    type: HitAreaType.Circle;
    x: number;
    y: number;
    radius: number;
};

export type EllipseHitArea = {
    type: HitAreaType.Ellipse;
    x: number;
    y: number;
    halfWidth: number;
    halfHeight: number;
};

export type PolygonHitArea = {
    type: HitAreaType.Polygon;
    path: string;
};

export type RoundedRectangleHitArea = {
    type: HitAreaType.RoundedRectangle;
    x: number;
    y: number;
    width: number;
    height: number;
    radius: number;
};

export type HitArea =
    | NoneHitArea
    | RectangleHitArea
    | CircleHitArea
    | EllipseHitArea
    | PolygonHitArea
    | RoundedRectangleHitArea;
